// ISO date arithmetic helpers. All dates are 'YYYY-MM-DD' strings;
// avoid Date objects except inside these helpers (timezone weirdness).

import type { ISODate } from './types';

const DAY_MS = 86_400_000;

function toUtcMs(iso: ISODate): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

function fromUtcMs(ms: number): ISODate {
  return new Date(ms).toISOString().slice(0, 10);
}

// Today's date on the viewer's own clock. `new Date().toISOString()`
// is the UTC date: in Hawaiʻi it is tomorrow from 2 PM, in Pacific time
// from 5 PM, and in Japan it is still yesterday until 9 AM (TODO #570).
// Also use it for a Date returned by a date picker (a local Date).
export function localDateISO(d: Date = new Date()): ISODate {
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function addDays(iso: ISODate, n: number): ISODate {
  return fromUtcMs(toUtcMs(iso) + n * DAY_MS);
}

export function daysBetween(start: ISODate, end: ISODate): number {
  return Math.round((toUtcMs(end) - toUtcMs(start)) / DAY_MS);
}

// 0-based day index of `iso` within [tripStart, tripEnd]. Returns -1
// if outside the range.
export function dayIdxOf(iso: ISODate, tripStart: ISODate, tripEnd: ISODate): number {
  if (iso < tripStart || iso > tripEnd) return -1;
  return daysBetween(tripStart, iso);
}

// Inclusive list of ISO dates from start through end.
export function isoRange(start: ISODate, end: ISODate): ISODate[] {
  const days = daysBetween(start, end);
  const out: ISODate[] = [];
  for (let i = 0; i <= days; i++) out.push(addDays(start, i));
  return out;
}

// Human-readable date — "Sat May 22, 2026". Stays in user locale.
export function formatLong(iso: ISODate): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// "May 22 – 26, 2026" / "Dec 28, 2026 – Jan 3, 2027" — range formatter.
export function formatRange(start: ISODate, end: ISODate): string {
  const ds = new Date(`${start}T00:00:00Z`);
  const de = new Date(`${end}T00:00:00Z`);
  const sameYear = ds.getUTCFullYear() === de.getUTCFullYear();
  const sameMonth = sameYear && ds.getUTCMonth() === de.getUTCMonth();
  const startStr = ds.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const endStr = de.toLocaleDateString(undefined, {
    month: sameMonth ? undefined : 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    timeZone: 'UTC',
  });
  const yearStr = sameYear ? `, ${ds.getUTCFullYear()}` : '';
  return `${startStr} – ${endStr}${yearStr}`;
}
